/**
 * Anti-aircraft fire — the original's `s\combat\aa.c`, ported.
 *
 * Every constant is READ OUT OF THE GAME, not invented: `AA.DAT` is 54 bytes
 * and `aa.c` reads it in four `fread`s of 34 + 6 + 6 + 8, which accounts for the
 * file exactly. The decode, the firing model and the live confirmation are in
 * [AD-010](../../../../docs/engineering/flak-design.md); the numbers themselves
 * live in `flak-shell.ts` beside their `AA.DAT` byte offsets, so any one of them
 * can be re-checked against the file in a line.
 *
 * The shape of the original, in one paragraph. A battery is a ground position
 * and a side, and it holds **five independent firing slots**. Each slot picks
 * one enemy aircraft, keeps it while it stays in the envelope, and fires on its
 * own countdown; five slots on one aeroplane is what produces the *string* of
 * bursts the manual describes. The envelope is **horizontal range plus an
 * altitude band** — not a sphere; that is measured, not assumed (a battery
 * engaged at 3D range 24 808 units while its stated range is 20 000, because
 * the horizontal component was 16 516). A burst is placed **ahead of the target
 * along the target's own nose axis**, by the shell's time of flight times the
 * target's speed, plus a bias and a scatter that both *shrink* as the slot
 * tracks the same aeroplane for longer — the game's version of gunners ranging
 * in. Whether a burst hurts is then **decided by that placement**
 * (`burstLethality`): the shell lingers through four animation frames and
 * damages anything inside the current frame's own effect radius. AD-010's first
 * pass read a die roll in `aa_fire` as the hit test; §9 corrects it — that roll
 * drives an AI stimulus, not damage. Flak and gunfire (AD-007g) agree after
 * all: both are geometry.
 *
 * Self-contained on purpose: batteries are not general "ground targets" (Lane C
 * owns those) and they are not shootable — nothing in `aa.c` gives a battery
 * health, and no `.WLD` placement references a flak object, so there is nothing
 * to shoot at even if we wanted it. They follow `targets.ts`'s shape instead:
 * a small spec, a small state, and pure functions over both.
 *
 * **The battery is still not shootable; its lights are.** AD-017 §11 stands the
 * port's own searchlights beside the guns as real entities
 * (`searchlight-posts.ts`), and this file's one concession to them is
 * `FlakTickInput.blindBatteries` — a set of ids whose guns may not range in
 * because there is nothing left to see the aeroplane by. Nothing else here
 * knows the lights exist; a battery has no new field, and a tick handed no set
 * behaves exactly as it did before the lights were shootable, which is what
 * keeps every daylight mission bit-identical.
 *
 * **Where the rest of it is.** This file is the *tick*: acquire, lay, fire,
 * resolve. The nouns it works on — the battery, its five slots, its envelope,
 * its fuse, the burst it throws — are `flak-battery.ts`, and the constants all
 * of it reads are `flak-shell.ts` beside their `AA.DAT` offsets. Everything is
 * re-exported from here, so "import the flak layer" is still one line.
 */

import { addVec3, rotateVec3, scaleVec3, BODY_FORWARD, BODY_RIGHT, BODY_UP, type Vec3 } from "../flight/math3d.ts";
import type { RngBank } from "../flight/rng.ts";
import { hitAspect } from "./geometry.ts";
import { allocateHit, type RoundHit } from "./hit-allocation.ts";
import {
  AA_DAT_FIELDS,
  FLAK_AIM,
  FLAK_TIMER_UNIT_SECONDS,
  FLAK_TUNABLES,
  burstLethality,
  combatLevelScale,
  type CombatLevel,
} from "./flak-shell.ts";

export {
  AA_DAT_FIELDS,
  AIRFRAME_DESTROY_DAMAGE_UNITS,
  COMBAT_LEVELS,
  FLAK_AIM,
  FLAK_BURST_DAMAGE_UNITS,
  FLAK_BURST_FRAME_SECONDS,
  FLAK_BURST_RADII_M,
  FLAK_CALIBRATION_PENDING,
  FLAK_CLASSES,
  FLAK_COMBAT_LEVEL_SCALE,
  FLAK_ENVELOPE,
  FLAK_SLOTS_PER_BATTERY,
  FLAK_TIMER_UNIT_SECONDS,
  FLAK_TUNABLES,
  burstLethality,
  combatLevelScale,
} from "./flak-shell.ts";
export type { BurstLethality, CombatLevel, FlakClass, FlakTunableKey } from "./flak-shell.ts";

// The nouns live next door (`flak-battery.ts`) and travel with the tick, so a
// consumer that wants "the flak layer" still has one import. The split is a
// size ceiling, not an API boundary.
import {
  flakEngages,
  fuseSecondsOf,
  horizontalRangeM,
  trackingLevel,
  type FlakBattery,
  type FlakBurst,
  type FlakSlot,
  type FlakTarget,
} from "./flak-battery.ts";

/** `base + (rand & mask)` timer units, as seconds: 6.45 s to 13.12 s per gun. */
function reloadSeconds(rng: RngBank): number {
  const jitter = rng.nextUint32("flak") & AA_DAT_FIELDS.reloadJitterMask;
  return (AA_DAT_FIELDS.reloadBaseUnits + jitter) * FLAK_TIMER_UNIT_SECONDS;
}

export {
  createFlakBattery,
  flakEngages,
  fuseSecondsOf,
  horizontalRangeM,
  trackingLevel,
  underFlakFire,
} from "./flak-battery.ts";
export type { FlakBattery, FlakBatterySpec, FlakBurst, FlakSlot, FlakTarget } from "./flak-battery.ts";

interface PlacedBurst {
  readonly position: Vec3;
  /** Scatter across the flight path — what the effect radius is measured against. */
  readonly missM: number;
  /** Along-track separation at detonation, i.e. this tracking level's bias. */
  readonly biasM: number;
}

/**
 * Where the shell goes off: ahead of the target along the target's *own* nose
 * axis by the lead plus this tracking level's bias, with lateral and vertical
 * scatter. Two draws on the `flak` stream, lateral first. Placing it in the
 * target's frame rather than the battery's is what makes the string of bursts
 * walk along the aeroplane's path, which is what the manual describes and what
 * a pilot sees.
 */
function placeBurst(target: FlakTarget, level: number, leadM: number, rng: RngBank): PlacedBurst {
  const scatter = FLAK_AIM.scatterM[level] ?? 0;
  const biasM = FLAK_AIM.biasM[level] ?? 0;
  const lateral = (rng.nextFloat("flak") * 2 - 1) * scatter;
  const vertical = (rng.nextFloat("flak") * 2 - 1) * scatter;
  const forward = leadM + biasM;
  const body = addVec3(
    addVec3(scaleVec3(BODY_RIGHT, lateral), scaleVec3(BODY_FORWARD, forward)),
    scaleVec3(BODY_UP, vertical),
  );
  return {
    position: addVec3(target.body.position, rotateVec3(target.body.orientation, body)),
    missM: Math.hypot(lateral, vertical),
    biasM,
  };
}

export interface FlakTickInput {
  readonly batteries: readonly FlakBattery[];
  readonly targets: readonly FlakTarget[];
  readonly rng: RngBank;
  readonly dt: number;
  /**
   * The Realism Panel's COMBAT LEVEL — player-only, AD-010 §9.4. `undefined`
   * behaves as the top setting (full damage), matching every encounter that
   * predates this control.
   */
  readonly combatLevel?: CombatLevel;
  /**
   * Batteries that cannot see — every searchlight serving them has been shot
   * out (`searchlight-posts.ts`, AD-010 §10 / AD-017 §11). Their guns fire, but
   * they never range in: see `rangingIn`.
   *
   * `undefined` means nobody is blind, which is **every daylight mission and
   * every encounter that has no lights in it at all**. Ranging in is a daylight
   * mechanic the original models and this port keeps unchanged; blindness is
   * only ever something the dark can do.
   */
  readonly blindBatteries?: ReadonlySet<string>;
}

export interface FlakTickResult {
  readonly batteries: readonly FlakBattery[];
  readonly bursts: readonly FlakBurst[];
  readonly hits: readonly RoundHit[];
}

function targetOf(targets: readonly FlakTarget[], id: string | null): FlakTarget | null {
  if (id === null) return null;
  return targets.find((entry) => entry.id === id) ?? null;
}

/**
 * Slot target selection, `aa.c`'s rule: walk the roster in order and take the
 * first engageable enemy **no other slot of this battery is already on**; if
 * every engageable enemy is already spoken for, pick one of them at random.
 * That is what puts all five guns on a lone aeroplane and spreads them over a
 * flight.
 *
 * `taken` is the battery's slots *as they stand part-way through this tick*, so
 * two slots acquiring on the same tick cannot both take the same aeroplane
 * while a second one goes unwatched.
 */
function acquire(
  battery: FlakBattery,
  taken: readonly (string | null)[],
  slotIndex: number,
  targets: readonly FlakTarget[],
  rng: RngBank,
): string | null {
  const engageable = targets.filter((target) => flakEngages(battery, target));
  if (engageable.length === 0) return null;
  const spoken = new Set(taken.filter((id, index) => id !== null && index !== slotIndex));
  const free = engageable.find((target) => !spoken.has(target.id));
  if (free) return free.id;
  const index = rng.nextUint32("flak") % engageable.length;
  return engageable[index]?.id ?? null;
}

const IDLE_SLOT: FlakSlot = { targetId: null, timerS: 0, trackingShots: 0 };

/** Everything one battery mutates while its five guns take their turn. */
interface BatteryPass {
  readonly battery: FlakBattery;
  readonly fuseS: number;
  readonly combatLevel: CombatLevel | undefined;
  /**
   * Whether this battery's guns may work up their aim at all this tick — false
   * only when the dark has taken its searchlights. See `stepSlot`.
   */
  readonly rangingIn: boolean;
  /** Slot targets as they stand part-way through the tick — `aa.c` mutates the live array. */
  readonly laid: (string | null)[];
  /** Ditto for tracking counts, which the ranged-in rule reaches across slots to clear. */
  readonly shots: number[];
  readonly settled: FlakSlot[];
  readonly bursts: FlakBurst[];
  readonly hits: RoundHit[];
}

/** One gun's shot: place it, see whether it hurt, and book the hit. */
function fireSlot(pass: BatteryPass, target: FlakTarget, level: number, rng: RngBank): void {
  const { battery } = pass;
  const placed = placeBurst(target, level, pass.fuseS * target.speedMps, rng);
  const effect = burstLethality(placed.missM, placed.biasM, target.speedMps);
  pass.bursts.push({
    batteryId: battery.id,
    targetId: target.id,
    position: placed.position,
    lifeS: pass.fuseS,
    lethal: effect.lethal,
    missM: placed.missM,
    trackingLevel: level,
  });
  if (!effect.lethal) return;
  // COMBAT LEVEL scales the player only (AD-010 §9.4); an AI target always
  // takes the burst's full share. At the lowest setting the scale is exactly
  // 0 — the original returns from `damage.c` before booking anything at all,
  // which this mirrors by not pushing a hit rather than pushing a zero one.
  const scale = target.isPlayer ? combatLevelScale(pass.combatLevel) : 1;
  if (scale <= 0) return;
  // A battery does not move, so where it stands *is* the arrival direction —
  // which for ground fire is almost always the belly.
  const aspect = hitAspect(target.body, battery.position);
  pass.hits.push({
    shooterId: battery.id,
    targetId: target.id,
    component: allocateHit(aspect, rng, "flak"),
    amount: (effect.inner ? FLAK_TUNABLES.innerBurstDamage : FLAK_TUNABLES.outerBurstDamage) * scale,
    rangeM: horizontalRangeM(battery, target),
    aspect,
  });
}

/**
 * A gun that has ranged in knocks every *other* gun of the battery off its own
 * count, so at most one of the five is ever shooting well at a given aeroplane.
 * This is the single loudest reason a battery threatens without dominating —
 * see AD-010 §9.3.
 */
function clearOtherGuns(pass: BatteryPass, slotIndex: number, targetId: string): void {
  for (let other = 0; other < pass.battery.slots.length; other++) {
    if (other === slotIndex || pass.laid[other] !== targetId) continue;
    pass.shots[other] = 0;
    const built = pass.settled[other];
    if (built) pass.settled[other] = { ...built, trackingShots: 0 };
  }
}

function idle(pass: BatteryPass, slotIndex: number): void {
  pass.laid[slotIndex] = null;
  pass.shots[slotIndex] = 0;
  pass.settled.push(IDLE_SLOT);
}

/**
 * One slot of one battery, for one tick.
 *
 * ## Ranging in, and the one thing that can stop it (AD-010 §10)
 *
 * `trackingShots` is the whole of the original's "gunners working up their
 * aim": it indexes the bias and scatter tables, and walking it from 0 to 2
 * takes a shell from 152 m of systematic lead error and 57 m of scatter down to
 * 7.6 m of each. Everything about how dangerous a battery is lives in that
 * walk.
 *
 * A battery whose searchlights are gone never takes a step of it. Not a
 * penalty applied to the aim, but the count itself pinned at zero — because the
 * count *is* the crew reading a range off something they can see, and there is
 * nothing to read one off. Three consequences fall out of it rather than being
 * written separately, which is why it is expressed this way: the shells stay at
 * level 0's bias and scatter; `clearOtherGuns` never fires, since no gun ever
 * reaches two shots, so a blind battery does not do the ranged-in gun's
 * knock-the-others-off dance it has no ranged-in gun for; and the `trackingCap`
 * hand-off never trips, so a blind gun stays on the aeroplane it can hear
 * instead of politely rotating. It fires as much as it ever did. It just never
 * gets closer.
 */
function stepSlot(pass: BatteryPass, slot: FlakSlot, slotIndex: number, input: FlakTickInput): void {
  // A target that has left the envelope is dropped and the gun starts over.
  const held = targetOf(input.targets, slot.targetId);
  const keeping = held !== null && flakEngages(pass.battery, held);
  const targetId = keeping
    ? slot.targetId
    : acquire(pass.battery, pass.laid, slotIndex, input.targets, input.rng);
  pass.laid[slotIndex] = targetId;

  const target = targetOf(input.targets, targetId);
  if (!target || targetId === null) return idle(pass, slotIndex);

  const trackingShots =
    keeping && pass.rangingIn ? Math.min(AA_DAT_FIELDS.trackingCap, pass.shots[slotIndex] ?? 0) : 0;
  pass.shots[slotIndex] = trackingShots;
  const timerS = slot.timerS - input.dt;
  if (timerS > 0) return void pass.settled.push({ targetId, timerS, trackingShots });

  // `AA.DAT` +0x00 is a *cap on how long one gun may stay on one aeroplane*,
  // not a saturation: `aa.c` tests it before it increments and lets the target
  // go when it is reached, so a gun works up to its best aim, holds it for a
  // few shells and then starts the whole approach again on whoever it finds.
  if (trackingShots >= AA_DAT_FIELDS.trackingCap) return idle(pass, slotIndex);

  const level = trackingLevel({ targetId, timerS, trackingShots });
  fireSlot(pass, target, level, input.rng);

  const nextShots = pass.rangingIn ? trackingShots + 1 : 0;
  pass.shots[slotIndex] = nextShots;
  pass.settled.push({ targetId, timerS: reloadSeconds(input.rng), trackingShots: nextShots });
  if (nextShots >= 2) clearOtherGuns(pass, slotIndex, targetId);
}

/**
 * One tick of every battery.
 *
 * Bursts are an *output*, not state, and that is a faithful simplification
 * rather than a shortcut: the original's burst lingers for four animation
 * frames and damages its target on every tick it is inside the current frame's
 * effect radius, so a shell that connects at all connects immediately and keeps
 * doing so for the whole of its short life. `burstLethality` integrates that
 * pass analytically into one hit, which is also the only form independent of
 * tick rate — and it leaves nothing to carry between ticks, so the hashed state
 * does not grow with the number of shells in the air.
 */
export function stepFlak(input: FlakTickInput): FlakTickResult {
  const bursts: FlakBurst[] = [];
  const hits: RoundHit[] = [];
  const batteries = input.batteries.map((battery) => {
    const pass: BatteryPass = {
      battery,
      fuseS: fuseSecondsOf(battery),
      combatLevel: input.combatLevel,
      rangingIn: !(input.blindBatteries?.has(battery.id) ?? false),
      laid: battery.slots.map((slot) => slot.targetId),
      shots: battery.slots.map((slot) => slot.trackingShots),
      settled: [],
      bursts,
      hits,
    };
    battery.slots.forEach((slot, slotIndex) => stepSlot(pass, slot, slotIndex, input));
    return { ...battery, slots: pass.settled };
  });

  return { batteries, bursts, hits };
}

